import { t } from '../../i18n/es';

export interface PauseControls {
  resume(): void;
  restartFromCheckpoint(): void;
  abandon(): void;
  openSettings(): void;
}

/** Keys that toggle the pause (GDD §5.5); inside the abandon confirmation they must not reach the game. */
const PAUSE_KEYS = new Set(['Escape', 'KeyP']);

/**
 * PAUSA (GDD §13.2, issue #60): a modal dialog over the frozen flight with "Reanudar", "Reiniciar desde el
 * checkpoint", "Ajustes" and the abandon button, focus on "Reanudar". Escape and P resume through the game
 * input (the dialog only stops its native close). Abandoning asks for confirmation in a second
 * dialog whose destructive button is the only red; Escape there goes back to the pause dialog.
 * Native <dialog> until the design system lands (#15).
 */
export function pauseDialog(controls: PauseControls): HTMLDialogElement {
  const dialog = document.createElement('dialog');
  dialog.className = 'dialog pause';
  dialog.setAttribute('aria-labelledby', 'pause-title');
  dialog.innerHTML = `
    <h2 id="pause-title" class="panel-title">${t('ui.pausa.title')}</h2>
    <div class="pause-actions">
      <button type="button" class="button" data-choice="resume">${t('ui.pausa.resume')}</button>
      <button type="button" class="button" data-choice="restart">${t('ui.pausa.restart')}</button>
      <button type="button" class="button" data-choice="settings">${t('ui.pausa.settings')}</button>
      <button type="button" class="button" data-choice="abandon">${t('ui.pausa.abandon')}</button>
    </div>`;
  dialog.addEventListener('cancel', (event) => event.preventDefault());

  const button = (choice: string) => dialog.querySelector<HTMLButtonElement>(`[data-choice="${choice}"]`)!;
  button('resume').addEventListener('click', () => controls.resume());
  button('restart').addEventListener('click', () => controls.restartFromCheckpoint());
  button('settings').addEventListener('click', () => controls.openSettings());
  button('abandon').addEventListener('click', () => confirmAbandon(dialog, button('abandon'), controls));

  queueMicrotask(() => {
    if (!dialog.isConnected) return;
    dialog.showModal();
    button('resume').focus();
  });
  return dialog;
}

function confirmAbandon(parent: HTMLDialogElement, opener: HTMLElement, controls: PauseControls): void {
  const confirm = document.createElement('dialog');
  confirm.className = 'dialog';
  confirm.setAttribute('aria-labelledby', 'abandon-title');
  confirm.innerHTML = `
    <h2 id="abandon-title" class="panel-title">${t('ui.pausa.abandon')}</h2>
    <p class="body">${t('ui.pausa.abandon.confirm')}</p>
    <div class="slot-actions">
      <button type="button" class="button" data-choice="cancel">${t('ui.pausa.abandon.cancel')}</button>
      <button type="button" class="button button--destructive" data-choice="confirm">${t('ui.pausa.abandon.action')}</button>
    </div>`;
  const close = (abandoned: boolean) => {
    if (!confirm.open) return;
    confirm.close();
    confirm.remove();
    if (abandoned) controls.abandon();
    else opener.focus();
  };
  // Escape and P belong to this dialog while it is open: they never resume the flight behind it.
  confirm.addEventListener('keydown', (event) => {
    if (!PAUSE_KEYS.has(event.code)) return;
    event.stopPropagation();
    if (event.code === 'Escape') {
      event.preventDefault();
      close(false);
    }
  });
  confirm.addEventListener('cancel', (event) => {
    event.preventDefault();
    close(false);
  });
  confirm.querySelector('[data-choice="cancel"]')!.addEventListener('click', () => close(false));
  confirm.querySelector('[data-choice="confirm"]')!.addEventListener('click', () => close(true));
  parent.after(confirm);
  confirm.showModal();
  confirm.querySelector<HTMLElement>('[data-choice="cancel"]')!.focus();
}
